import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { isPast, isToday } from 'date-fns'

import { CacheKeys } from '../lib/cache'
import { toCamelCase, toSnakeCase } from '../lib/typeHelper'
import { automationFollowUpUrl } from './outboundCallAutomation'

export interface ReminderLead {
  id: string
  name: string
}

/** `metadata.kind` values the UI acts on. Mirrors the backend's kind constants. */
export enum ReminderKind {
  FOLLOW_UP = 'follow_up',
  AUTOMATION_FOLLOW_UP = 'automation_follow_up',
}

export interface ReminderAction {
  /** In-app route the reminder's CTA opens. */
  to: string
  label: string
}

export interface Reminder {
  id: string
  message: string
  reminderDate: string // ISO8601 (UTC)
  completedAt: string | null // ISO8601 (UTC) when completed, else null
  metadata: Record<string, unknown>
  lead: ReminderLead | null
}

/**
 * The reminder's in-app CTA, or null when there's nothing to open. Mirrors the
 * CTA the reminder emails render, so the toast, the detail dialog and the email
 * all take the rep to the same place.
 */
export function reminderAction(reminder: Reminder): ReminderAction | null {
  if (reminder.lead) return { to: `/insure/internal/sales-leads/${reminder.lead.id}`, label: 'View lead' }
  const { kind, automationId } = reminder.metadata as { kind?: string; automationId?: string }
  if (kind === ReminderKind.AUTOMATION_FOLLOW_UP && automationId) {
    return { to: automationFollowUpUrl(automationId), label: 'Start follow-up' }
  }
  return null
}

const REFETCH_INTERVAL_MS = 10 * 60 * 1000 // 10 minutes

function getMyReminders(): Promise<Reminder[]> {
  return axios
    .get('/api/v1/insure/internal/reminders/')
    .then((response) => toCamelCase(response.data) as Reminder[])
}

// Not completed, today, and not already passed — the nav dot signals an upcoming
// reminder still to act on, not one that already fired or is done.
export function hasReminderToday(reminders: Reminder[]): boolean {
  return reminders.some((reminder) => {
    const date = new Date(reminder.reminderDate)
    return !reminder.completedAt && isToday(date) && !isPast(date)
  })
}

export function useMyReminders({ enabled = true }: { enabled?: boolean } = {}) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.MY_REMINDERS],
    queryFn: getMyReminders,
    enabled,
    refetchInterval: REFETCH_INTERVAL_MS,
  })
  return { reminders: data ?? [], isLoading }
}

export function useCompleteReminder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      axios
        .post(`/api/v1/insure/internal/reminders/${id}/complete/`)
        .then((response) => toCamelCase(response.data) as Reminder),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.MY_REMINDERS] })
    },
  })
}

export function useCreateReminder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ message, reminderDate }: { message: string; reminderDate: string }) =>
      axios
        .post(
          '/api/v1/insure/internal/reminders/',
          toSnakeCase({
            message,
            reminderDate,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          })
        )
        .then((response) => toCamelCase(response.data) as Reminder),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.MY_REMINDERS] })
    },
  })
}
